import { useGameStore } from '../state/useGameStore';
import { getArmorProfile } from '../shared/game';
import { WEAPON_MECHANICS } from '../shared/constants';

/**
 * Phase 171 — Laser-rifle sniper scope overlay. Renders a darkened vignette with a
 * fine crosshair and a charge readout whenever the local player is aiming down sights
 * with the laser rifle. The camera FOV zoom is handled separately in camera.ts.
 */
export function SniperScope() {
  const localPlayer = useGameStore((s) => s.localPlayer);

  const ads = Boolean((localPlayer as { ads?: boolean }).ads);
  const weaponId = (localPlayer as { selectedWeapon?: string | null }).selectedWeapon ?? null;
  if (!ads || weaponId !== 'laser-rifle') {
    return null;
  }

  const armorClass = (localPlayer as { armorClass?: 'light' | 'medium' | 'heavy' | null }).armorClass ?? 'medium';
  const maxEnergy = getArmorProfile(armorClass).maxEnergy || 1;
  const energy = Math.max(0, Math.min(maxEnergy, (localPlayer as { energy?: number }).energy ?? maxEnergy));
  const chargeRatio = energy / maxEnergy;
  const { minChargeMultiplier, maxChargeMultiplier, energyCost } = WEAPON_MECHANICS.laserRifle;
  const chargeMultiplier = minChargeMultiplier + (maxChargeMultiplier - minChargeMultiplier) * chargeRatio;
  const canFire = energy >= energyCost;
  const accent = canFire ? '#7dff9c' : '#ff8d74';

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 206,
        // Circular scope vignette — clear center, heavy darkening at the edges.
        background:
          'radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 26%, rgba(0,0,0,0.35) 34%, rgba(0,0,0,0.92) 46%)',
      }}
    >
      {/* Scope ring */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '46vh',
          height: '46vh',
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          border: `1px solid ${accent}55`,
          boxShadow: `0 0 0 1px rgba(0,0,0,0.6), inset 0 0 40px ${accent}22`,
        }}
      />
      {/* Fine crosshair */}
      <div style={crosshairLine('h', accent)} />
      <div style={crosshairLine('v', accent)} />
      {/* Center dot */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 3,
          height: 3,
          marginLeft: -1.5,
          marginTop: -1.5,
          borderRadius: '50%',
          background: accent,
        }}
      />
      {/* Charge readout */}
      <div
        style={{
          position: 'absolute',
          top: 'calc(50% + 24vh)',
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: 'monospace',
          fontSize: 12,
          letterSpacing: 1,
          color: accent,
          textShadow: '0 0 4px rgba(0,0,0,0.9)',
          textAlign: 'center',
        }}
      >
        <div>CHARGE ×{chargeMultiplier.toFixed(2)}</div>
        <div style={{ opacity: 0.7 }}>{canFire ? 'READY' : 'LOW ENERGY'}</div>
      </div>
    </div>
  );
}

function crosshairLine(axis: 'h' | 'v', color: string): React.CSSProperties {
  const common: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    background: `${color}aa`,
  };
  if (axis === 'h') {
    return { ...common, width: '18vh', height: 1, transform: 'translate(-50%, -50%)' };
  }
  return { ...common, width: 1, height: '18vh', transform: 'translate(-50%, -50%)' };
}
